<template>
  <div class="dashboard-container">
    <!-- Título Principal -->
    <div class="dashboard-header">
      <h1 class="modern-title">Dashboard</h1>
      <p class="modern-subtitle">Visão geral do sistema</p>
    </div>

    <!-- Filtros de Período -->
    <div class="filters-section-loose">
      <div class="filters-header">
        <h3 class="filters-title">
          <i class="fa-solid fa-filter"></i>
          Filtros de Período
        </h3>
      </div>
      
      <div class="filters-grid-loose">
        <div class="input-group">
          <label class="input-label">
            <i class="fa-solid fa-calendar-days"></i>
            Data Início
          </label>
          <input 
            class="form-input" 
            type="date" 
            v-model="dateBegin"
            @change="loadDashboardData"
          />
        </div>

        <div class="input-group">
          <label class="input-label">
            <i class="fa-solid fa-calendar-days"></i>
            Data Fim
          </label>
          <input 
            class="form-input" 
            type="date" 
            v-model="dateEnd"
            @change="loadDashboardData"
          />
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando dados do dashboard...</span>
      </div>
    </div>

    <!-- PRIMEIRA LINHA: Resumo Financeiro -->
    <div v-show="!loading" class="dashboard-section">
      <h2 class="section-title">
        <i class="fa-solid fa-chart-line"></i>
        Resumo Financeiro
      </h2>
      
      <div class="dashboard-stats">
        <!-- Card Saldo Anterior -->
        <div class="dashboard-card financial-card">
          <div class="dashboard-card-icon">
            <i class="fa-solid fa-history"></i>
          </div>
          <div class="dashboard-card-title">Saldo Anterior</div>
          <div class="dashboard-card-value">{{ setCurrencyString(financialSummary?.previousBalance || 0) }}</div>
          <div class="dashboard-card-subtitle">Saldo inicial do período</div>
        </div>

        <!-- Card Vendas -->
        <div class="dashboard-card financial-card">
          <div class="dashboard-card-icon positive">
            <i class="fa-solid fa-arrow-up"></i>
          </div>
          <div class="dashboard-card-title">Vendas</div>
          <div class="dashboard-card-value positive">{{ setCurrencyString(financialSummary?.sales || 0) }}</div>
          <div class="dashboard-card-subtitle">Total de vendas (+)</div>
        </div>

        <!-- Card Comissão -->
        <div class="dashboard-card financial-card">
          <div class="dashboard-card-icon negative">
            <i class="fa-solid fa-percent"></i>
          </div>
          <div class="dashboard-card-title">Comissão</div>
          <div class="dashboard-card-value negative">{{ setCurrencyString(financialSummary?.commission || 0) }}</div>
          <div class="dashboard-card-subtitle">Comissões pagas (-)</div>
        </div>

        <!-- Card Líquido -->
        <div class="dashboard-card financial-card">
          <div class="dashboard-card-icon" :class="{ 'positive': (financialSummary?.net || 0) >= 0, 'negative': (financialSummary?.net || 0) < 0 }">
            <i class="fa-solid fa-chart-line"></i>
          </div>
          <div class="dashboard-card-title">Líquido</div>
          <div class="dashboard-card-value" :class="{ 'positive': (financialSummary?.net || 0) >= 0, 'negative': (financialSummary?.net || 0) < 0 }">
            {{ setCurrencyString(financialSummary?.net || 0) }}
          </div>
          <div class="dashboard-card-subtitle">Vendas - Comissão</div>
        </div>

        <!-- Card Prêmios Retidos -->
        <div class="dashboard-card financial-card">
          <div class="dashboard-card-icon warning">
            <i class="fa-solid fa-clock"></i>
          </div>
          <div class="dashboard-card-title">Prêmios Retidos</div>
          <div class="dashboard-card-value warning">{{ setCurrencyString(financialSummary?.retainedPrizes || 0) }}</div>
          <div class="dashboard-card-subtitle">Prêmios não resgatados</div>
        </div>

        <!-- Card Prêmios Pagos -->
        <div class="dashboard-card financial-card">
          <div class="dashboard-card-icon negative">
            <i class="fa-solid fa-trophy"></i>
          </div>
          <div class="dashboard-card-title">Prêmios Pagos</div>
          <div class="dashboard-card-value negative">{{ setCurrencyString(financialSummary?.prizes || 0) }}</div>
          <div class="dashboard-card-subtitle">Prêmios já resgatados (-)</div>
        </div>

        <!-- Card L.P (Líquido com Prêmios) -->
        <div class="dashboard-card financial-card">
          <div class="dashboard-card-icon" :class="{ 'positive': (financialSummary?.netWithPrize || 0) >= 0, 'negative': (financialSummary?.netWithPrize || 0) < 0 }">
            <i class="fa-solid fa-coins"></i>
          </div>
          <div class="dashboard-card-title">L.P</div>
          <div class="dashboard-card-value" :class="{ 'positive': (financialSummary?.netWithPrize || 0) >= 0, 'negative': (financialSummary?.netWithPrize || 0) < 0 }">
            {{ setCurrencyString(financialSummary?.netWithPrize || 0) }}
          </div>
          <div class="dashboard-card-subtitle">Líquido com Prêmios</div>
        </div>

        <!-- Card Banca Recebeu -->
        <div class="dashboard-card financial-card">
          <div class="dashboard-card-icon positive">
            <i class="fa-solid fa-hand-holding-dollar"></i>
          </div>
          <div class="dashboard-card-title">Banca Recebeu</div>
          <div class="dashboard-card-value positive">{{ setCurrencyString(financialSummary?.credit || 0) }}</div>
          <div class="dashboard-card-subtitle">Entradas da banca (+)</div>
        </div>

        <!-- Card Banca Pagou -->
        <div class="dashboard-card financial-card">
          <div class="dashboard-card-icon negative">
            <i class="fa-solid fa-hand-holding-usd"></i>
          </div>
          <div class="dashboard-card-title">Banca Pagou</div>
          <div class="dashboard-card-value negative">{{ setCurrencyString(financialSummary?.debit || 0) }}</div>
          <div class="dashboard-card-subtitle">Saídas da banca (-)</div>
        </div>

        <!-- Card Saldo Atual -->
        <div class="dashboard-card financial-card">
          <div class="dashboard-card-icon" :class="{ 'positive': (financialSummary?.currentBalance || 0) >= 0, 'negative': (financialSummary?.currentBalance || 0) < 0 }">
            <i class="fa-solid fa-wallet"></i>
          </div>
          <div class="dashboard-card-title">Saldo Atual</div>
          <div class="dashboard-card-value" :class="{ 'positive': (financialSummary?.currentBalance || 0) >= 0, 'negative': (financialSummary?.currentBalance || 0) < 0 }">
            {{ setCurrencyString(financialSummary?.currentBalance || 0) }}
          </div>
          <div class="dashboard-card-subtitle">Saldo final do período</div>
        </div>
      </div>

      <!-- Gráfico Financeiro -->
      <div class="chart-container financial-chart">
        <div class="chart-header">
          <h3>Resumo Financeiro - {{ getPeriodLabel() }}</h3>
        </div>
        <div class="chart-content">
          <div v-if="balanceData && balanceData.regions && balanceData.regions.length > 0" class="chart-bars">
            <div class="chart-bar">
              <div class="bar-label">Saldo Anterior</div>
              <div class="bar-container">
                <div class="bar neutral" :style="{ height: getBarHeight(financialSummary?.previousBalance || 0) }"></div>
              </div>
              <div class="bar-value">{{ setCurrencyString(financialSummary?.previousBalance || 0) }}</div>
            </div>
            <div class="chart-bar">
              <div class="bar-label">Vendas(+)</div>
              <div class="bar-container">
                <div class="bar positive" :style="{ height: getBarHeight(financialSummary?.sales || 0) }"></div>
              </div>
              <div class="bar-value">{{ setCurrencyString(financialSummary?.sales || 0) }}</div>
            </div>
            <div class="chart-bar">
              <div class="bar-label">Comissão(-)</div>
              <div class="bar-container">
                <div class="bar negative" :style="{ height: getBarHeight(Math.abs(financialSummary?.commission || 0)) }"></div>
              </div>
              <div class="bar-value">{{ setCurrencyString(financialSummary?.commission || 0) }}</div>
            </div>
            <div class="chart-bar">
              <div class="bar-label">Líquido</div>
              <div class="bar-container">
                <div class="bar" :class="{ 'positive': (financialSummary?.net || 0) >= 0, 'negative': (financialSummary?.net || 0) < 0 }" 
                     :style="{ height: getBarHeight(Math.abs(financialSummary?.net || 0)) }"></div>
              </div>
              <div class="bar-value">{{ setCurrencyString(financialSummary?.net || 0) }}</div>
            </div>
            <div class="chart-bar">
              <div class="bar-label">Prêmios Retidos</div>
              <div class="bar-container">
                <div class="bar warning" :style="{ height: getBarHeight(Math.abs(financialSummary?.retainedPrizes || 0)) }"></div>
              </div>
              <div class="bar-value">{{ setCurrencyString(financialSummary?.retainedPrizes || 0) }}</div>
            </div>
            <div class="chart-bar">
              <div class="bar-label">Prêmios Pagos(-)</div>
              <div class="bar-container">
                <div class="bar negative" :style="{ height: getBarHeight(Math.abs(financialSummary?.prizes || 0)) }"></div>
              </div>
              <div class="bar-value">{{ setCurrencyString(financialSummary?.prizes || 0) }}</div>
            </div>
            <div class="chart-bar">
              <div class="bar-label">L.P</div>
              <div class="bar-container">
                <div class="bar" :class="{ 'positive': (financialSummary?.netWithPrize || 0) >= 0, 'negative': (financialSummary?.netWithPrize || 0) < 0 }" 
                     :style="{ height: getBarHeight(Math.abs(financialSummary?.netWithPrize || 0)) }"></div>
              </div>
              <div class="bar-value">{{ setCurrencyString(financialSummary?.netWithPrize || 0) }}</div>
            </div>
            <div class="chart-bar">
              <div class="bar-label">Banca Recebeu(+)</div>
              <div class="bar-container">
                <div class="bar positive" :style="{ height: getBarHeight(financialSummary?.credit || 0) }"></div>
              </div>
              <div class="bar-value">{{ setCurrencyString(financialSummary?.credit || 0) }}</div>
            </div>
            <div class="chart-bar">
              <div class="bar-label">Banca Pagou(-)</div>
              <div class="bar-container">
                <div class="bar negative" :style="{ height: getBarHeight(Math.abs(financialSummary?.debit || 0)) }"></div>
              </div>
              <div class="bar-value">{{ setCurrencyString(financialSummary?.debit || 0) }}</div>
            </div>
            <div class="chart-bar">
              <div class="bar-label">Saldo Atual</div>
              <div class="bar-container">
                <div class="bar" :class="{ 'positive': (financialSummary?.currentBalance || 0) >= 0, 'negative': (financialSummary?.currentBalance || 0) < 0 }" 
                     :style="{ height: getBarHeight(Math.abs(financialSummary?.currentBalance || 0)) }"></div>
              </div>
              <div class="bar-value">{{ setCurrencyString(financialSummary?.currentBalance || 0) }}</div>
            </div>
          </div>
          <div v-else class="chart-placeholder">
            <i class="fa-solid fa-chart-line chart-placeholder-icon"></i>
            <p>Nenhum dado financeiro encontrado para o período selecionado</p>
          </div>
        </div>
      </div>
    </div>

    <!-- SEGUNDA LINHA: Vendedores -->
    <div v-show="!loading" class="dashboard-section">
      <h2 class="section-title">
        <i class="fa-solid fa-users"></i>
        Vendedores
      </h2>
      
      <div class="dashboard-stats">
        <!-- Card Vendedores Ativos -->
        <div class="dashboard-card sellers-card">
          <div class="dashboard-card-icon">
            <i class="fa-solid fa-user-check"></i>
          </div>
          <div class="dashboard-card-title">Vendedores Ativos</div>
          <div class="dashboard-card-value">{{ sellersSummary?.totalActive || 0 }}</div>
          <div class="dashboard-card-subtitle">Total de vendedores ativos</div>
        </div>

        <!-- Card Ativos com Vendas -->
        <div class="dashboard-card sellers-card">
          <div class="dashboard-card-icon positive">
            <i class="fa-solid fa-user-tag"></i>
          </div>
          <div class="dashboard-card-title">Ativos com Vendas</div>
          <div class="dashboard-card-value positive">{{ sellersSummary?.activeWithSales || 0 }}</div>
          <div class="dashboard-card-subtitle">Vendedores que venderam no período</div>
        </div>

        <!-- Card Ativos sem Vendas -->
        <div class="dashboard-card sellers-card">
          <div class="dashboard-card-icon warning">
            <i class="fa-solid fa-user-minus"></i>
          </div>
          <div class="dashboard-card-title">Ativos sem Vendas</div>
          <div class="dashboard-card-value warning">{{ sellersSummary?.activeWithoutSales || 0 }}</div>
          <div class="dashboard-card-subtitle">Vendedores sem vendas no período</div>
        </div>
      </div>

      <!-- Gráfico de Vendedores -->
      <div class="chart-container sellers-chart">
        <div class="chart-header">
          <h3>Situação dos Vendedores - {{ getPeriodLabel() }}</h3>
        </div>
        <div class="chart-content">
          <div v-if="(sellersSummary?.totalActive || 0) > 0" class="pie-chart-container">
            <div 
              class="pie-chart"
              :style="{ 
                '--percentage-with-sales': getPercentage(sellersSummary?.activeWithSales || 0, sellersSummary?.totalActive || 0),
                '--percentage-without-sales': getPercentage(sellersSummary?.activeWithoutSales || 0, sellersSummary?.totalActive || 0)
              }"
            ></div>
            <div class="pie-legend">
              <div class="legend-item">
                <div class="legend-color active-with-sales"></div>
                <span>Com Vendas ({{ sellersSummary?.activeWithSales || 0 }})</span>
              </div>
              <div class="legend-item">
                <div class="legend-color active-without-sales"></div>
                <span>Sem Vendas ({{ sellersSummary?.activeWithoutSales || 0 }})</span>
              </div>
            </div>
          </div>
          <div v-else class="chart-placeholder">
            <i class="fa-solid fa-users chart-placeholder-icon"></i>
            <p>Nenhum vendedor ativo encontrado</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";

export default {
  name: "ViewDashboard",
  data() {
    return {
      loading: true,
      dateBegin: "",
      dateEnd: "",
      regions: [],
      currentUser: null,
      userResponsibleRegions: [],
      userBancaId: null,
      // UUID da banca ONLINE (mesmo usado no RegionSelect)
      ONLINE_BANCA_UUID: "e9c624bd-8403-4b8c-9ba1-ca4954833e93",
      
      // Dados financeiros
      financialSummary: {
        previousBalance: 0,
        sales: 0,
        commission: 0,
        net: 0,
        retainedPrizes: 0,
        prizes: 0,
        netWithPrize: 0,
        credit: 0,
        debit: 0,
        currentBalance: 0
      },
      
      // Dados de vendedores
      sellersSummary: {
        totalActive: 0,
        activeWithSales: 0,
        activeWithoutSales: 0
      },
      
      // Dados brutos do balanço
      balanceData: null,
      sellersData: []
    };
  },
  computed: {
    isAdmin() {
      return this.currentUser && this.currentUser.role === 0; // ADMIN
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === 3; // BANCA
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === 1; // GERENTE
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === 2; // COBRADOR
    },

    currentSelectedBanca() {
      const savedBanca = localStorage.getItem('selectedBanca');
      return savedBanca ? JSON.parse(savedBanca) : null;
    },

    availableRegions() {
      const currentRegion = JSON.parse(localStorage.getItem('currentRegion') || 'null');
      
      if (this.isAdmin) {
        // ADMIN: se há região selecionada, usar apenas ela
        if (currentRegion) {
          return this.regions.filter(region => region.id === currentRegion.id);
        }

        // Sem região selecionada: respeitar a banca escolhida no header
        if (this.currentSelectedBanca) {
          return this.regions.filter(region =>
            String(region.bancaId) === String(this.currentSelectedBanca.id)
          );
        }

        // "Todas as Bancas": excluir regiões da banca ONLINE quando marcado
        if (localStorage.getItem('excludeOnlineBanca') === 'true') {
          return this.regions.filter(region =>
            String(region.bancaId) !== this.ONLINE_BANCA_UUID
          );
        }

        return this.regions; // Todas as regiões quando nada está selecionado
      } else if (this.isBanca) {
        // BANCA: mostrar todas as regiões vinculadas à banca
        // Se há região selecionada, usar apenas ela; senão, usar todas as regiões da banca
        if (currentRegion) {
          return [currentRegion];
        } else {
          // Retornar todas as regiões que a banca tem acesso (filtradas pelo RegionSelect)
          const bancaRegions = this.regions.filter(region => region.bancaId === this.userBancaId);
          return bancaRegions;
        }
      } else if (this.isGerente) {
        // GERENTE: mostrar apenas dados da região do gerente
        // Usar a região principal do usuário
        if (this.currentUser && this.currentUser.regionId) {
          const userMainRegion = this.regions.find(region => region.id === this.currentUser.regionId);
          if (userMainRegion) {
            return [userMainRegion];
          }
        }
        
        // Fallback: usar primeira região responsável do usuário
        const firstResponsibleRegion = this.regions.find(region => 
          this.userResponsibleRegions.includes(region.id)
        );
        return firstResponsibleRegion ? [firstResponsibleRegion] : [];
      } else if (this.isCobrador) {
        // COBRADOR: regiões responsáveis; se houver região ativa no header, usa só ela
        if (currentRegion) {
          return [currentRegion];
        }
        return this.regions.filter(region =>
          this.userResponsibleRegions.includes(region.id)
        );
      } else {
        // Para outros roles, usar regiões responsáveis do usuário
        return this.regions.filter(region => 
          this.userResponsibleRegions.includes(region.id)
        );
      }
    }
  },
  async mounted() {
    this.loading = true;
    try {
      await this.loadCurrentUser();
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
    
    // Criar função nomeada para o listener de storage
    this.handleStorageChange = (e) => {
      if (e.key === 'currentRegion' || e.key === 'selectedBanca') {
        // Recarregar dados quando a região ou banca selecionada mudar
        // Aguardar um pouco para garantir que o computed availableRegions seja atualizado
        setTimeout(() => {
          this.loadDashboardData();
        }, 50);
      }
    };
    
    // Função para lidar com mudança manual de região
    this.handleRegionChanged = (e) => {
      // Aguardar um pouco para garantir que availableRegions seja atualizado
      setTimeout(() => {
        this.loadDashboardData();
      }, 50);
    };
    
    // Função para lidar com auto-seleção de região
    this.handleRegionAutoSelected = (e) => {
      // Aguardar um pouco para garantir que availableRegions seja atualizado
      setTimeout(() => {
        this.loadDashboardData();
      }, 200);
    };
    
    // Função para lidar com mudança de banca (select do header)
    this.handleBancaChanged = (e) => {
      // Aguardar um pouco para garantir que availableRegions seja atualizado
      setTimeout(() => {
        this.loadDashboardData();
      }, 50);
    };
    
    // Listeners para mudanças na região/banca
    window.addEventListener('storage', this.handleStorageChange);
    window.addEventListener('regionChanged', this.handleRegionChanged);
    window.addEventListener('regionAutoSelected', this.handleRegionAutoSelected);
    window.addEventListener('bancaChanged', this.handleBancaChanged);
  },
  
  beforeUnmount() {
    // Remover listeners ao destruir o componente
    if (this.handleStorageChange) {
      window.removeEventListener('storage', this.handleStorageChange);
    }
    if (this.handleRegionChanged) {
      window.removeEventListener('regionChanged', this.handleRegionChanged);
    }
    if (this.handleRegionAutoSelected) {
      window.removeEventListener('regionAutoSelected', this.handleRegionAutoSelected);
    }
    if (this.handleBancaChanged) {
      window.removeEventListener('bancaChanged', this.handleBancaChanged);
    }
  },
  methods: {
    // Função auxiliar para formatar data no timezone local (sem conversão UTC)
    formatDateLocal(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
          
          if (this.isAdmin) {
            // ADMIN pode ver todas as regiões
            this.initializeDates();
            await this.loadRegions();
          } else if (this.isBanca) {
            // BANCA: carregar o BancaId primeiro, depois carregar regiões
            await this.loadUserBancaId();
            this.initializeDates();
            await this.loadRegions();
          } else if (this.isGerente) {
            // GERENTE: usar região principal do usuário, mas ainda carregar regiões responsáveis como fallback
            await this.loadUserResponsibleRegions();
            this.initializeDates();
            await this.loadRegions();
          } else {
            // Para outros roles, carregar regiões do usuário primeiro
            await this.loadUserResponsibleRegions();
            this.initializeDates();
            await this.loadRegions();
          }
        } else {
          this.loading = false;
          toast.error("Usuário não encontrado. Faça login novamente.");
        }
      } catch (error) {
        this.loading = false;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserBancaId() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserBancaId(this.currentUser.id);
        this.userBancaId = response.data;
      } catch (error) {
        this.userBancaId = null;
        toast.error("Erro ao carregar BancaId do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = response.data.map(region => region.id);
        
        // Incluir a região principal também, se existir
        if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch (error) {
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    initializeDates() {
      const today = new Date();
      this.dateEnd = this.formatDateLocal(today);
      this.dateBegin = this.formatDateLocal(today);
    },

    async loadRegions() {
      // Aguardar um pouco para que o RegionSelect tenha tempo de carregar
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data || [];
        
        // Aguardar mais um pouco para que as regiões sejam processadas pelo RegionSelect
        await new Promise(resolve => setTimeout(resolve, 300));
        
        if (this.availableRegions.length > 0) {
          await this.loadDashboardData();
        } else {
          // Para usuário BANCA, verificar se tem regiões vinculadas
          if (this.isBanca) {
            const currentRegion = JSON.parse(localStorage.getItem('currentRegion') || 'null');
            if (!currentRegion) {
              // Verificar se existem regiões da banca
              const bancaRegions = this.regions.filter(region => region.bancaId === this.userBancaId);
              if (bancaRegions.length > 0) {
                // RegionSelect ainda está carregando, aguardar mais um pouco
                await new Promise(resolve => setTimeout(resolve, 1000));
                if (this.availableRegions.length > 0) {
                  await this.loadDashboardData();
                  return;
                }
              }
            }
          }
          
          this.loading = false;
          // Remover toast de warning para evitar spam
          // toast.warning("Nenhuma região disponível para consulta");
        }
      } catch (error) {
        this.loading = false;
        toast.error(handleErrorMessage(error));
        this.regions = [];
      }
    },

    async loadDashboardData() {
      if (!this.dateBegin || !this.dateEnd) {
        this.loading = false;
        toast.warning("Selecione o período para consulta!");
        return;
      }

      const regionsToUse = this.availableRegions;
      if (!regionsToUse || regionsToUse.length === 0) {
        this.loading = false;
        return;
      }

      this.loading = true;
      
      try {
        // Carregar dados em paralelo
        await Promise.all([
          this.loadFinancialData(),
          this.loadSellersData()
        ]);
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.resetAllData();
      } finally {
        this.loading = false;
      }
    },

    async loadFinancialData() {
      try {
        // COBRADOR: consolidar só pelos vendedores vinculados
        if (this.isCobrador) {
          await this.loadCobradorFinancialData();
          return;
        }

        const regionsToUse = this.availableRegions;
        if (!regionsToUse || regionsToUse.length === 0) {
          this.resetFinancialSummary();
          return;
        }

        const request = {
          regionIds: regionsToUse.map(r => r.id),
          balanceDateBegin: this.dateBegin + 'T00:00:00',
          balanceDateEnd: this.dateEnd + 'T23:59:59',
        };

        const response = await mainService.searchBalance(request);
        this.balanceData = response.data || null;
        
        this.calculateFinancialSummary();
      } catch (error) {
        this.resetFinancialSummary();
        throw error;
      }
    },

    /** Financeiro do cobrador: agrega searchSellerBalance só dos vendedores vinculados. */
    async loadCobradorFinancialData() {
      if (!this.currentUser?.id) {
        this.resetFinancialSummary();
        return;
      }

      const sellersRes = await mainService.getSellersByCollector(this.currentUser.id);
      let linked = Array.isArray(sellersRes.data) ? sellersRes.data : [];

      const regionIds = new Set(
        (this.availableRegions || []).map(r => String(r.id))
      );
      if (regionIds.size > 0) {
        linked = linked.filter(s => regionIds.has(String(s.regionId)));
      }

      if (linked.length === 0) {
        this.balanceData = null;
        this.resetFinancialSummary();
        return;
      }

      const response = await mainService.searchSellerBalance({
        sellerIds: linked.map(s => s.id),
        balanceDateBegin: this.dateBegin + 'T00:00:00',
        balanceDateEnd: this.dateEnd + 'T23:59:59',
      });

      const rows = response.data?.sellers || [];
      const sum = (key) =>
        rows.reduce((acc, row) => acc + Number(row?.[key] || 0), 0);

      const previousBalance = sum('previousBalance');
      const sales = sum('bet');
      const commission = sum('comission');
      const net = sum('net');
      const retainedPrizes = sum('retainedPrizes');
      const prizes = sum('totalAwarded');
      const credit = sum('credit');
      const debit = sum('debit');
      const currentBalance = sum('currentBalance');
      const netWithPrize = rows.reduce((acc, row) => {
        const n = Number(row?.net || 0);
        const p = Number(row?.totalAwarded || 0);
        return acc + (n - p);
      }, 0);

      this.balanceData = {
        previousBalanceAmount: previousBalance,
        betAmount: sales,
        comissionAmount: commission,
        netAmount: net,
        retainedPrizes,
        totalAwarded: prizes,
        netWithPrizeAmount: netWithPrize,
        creditAmount: credit,
        debitAmount: debit,
        currentBalanceAmount: currentBalance,
      };
      this.calculateFinancialSummary();
    },

    async loadSellersData() {
      try {
        // COBRADOR: apenas vendedores vinculados
        if (this.isCobrador) {
          if (!this.currentUser?.id) {
            this.resetSellersSummary();
            return;
          }

          const response = await mainService.getSellersByCollector(this.currentUser.id);
          let list = Array.isArray(response.data) ? response.data : [];

          const regionIds = new Set(
            (this.availableRegions || []).map(r => String(r.id))
          );
          if (regionIds.size > 0) {
            list = list.filter(s => regionIds.has(String(s.regionId)));
          }

          this.sellersData = list.map(s => ({
            ...s,
            collectorId: s.collectorId || s.CollectorId || this.currentUser.id,
          }));

          await this.calculateSellersSummary();
          return;
        }

        const regionsToUse = this.availableRegions;
        if (!regionsToUse || regionsToUse.length === 0) {
          this.resetSellersSummary();
          return;
        }

        // Buscar vendedores das regiões disponíveis para o usuário
        const allSellersPromises = regionsToUse.map(region => 
          mainService.searchSellersByRegion(region.id)
        );
        
        const responses = await Promise.all(allSellersPromises);
        
        // Combinar todos os vendedores em um array único
        this.sellersData = responses.reduce((acc, response) => {
          return acc.concat(response.data || []);
        }, []);
        
        // Remover duplicatas baseado no ID (caso um vendedor apareça em múltiplas regiões)
        this.sellersData = this.sellersData.filter((seller, index, self) => 
          index === self.findIndex(s => s.id === seller.id)
        );
        
        await this.calculateSellersSummary();
      } catch (error) {
        this.resetSellersSummary();
        throw error;
      }
    },

    calculateFinancialSummary() {
      try {
        if (!this.balanceData || typeof this.balanceData !== 'object') {
          this.resetFinancialSummary();
          return;
        }

        this.financialSummary = {
          previousBalance: this.balanceData.previousBalanceAmount || 0,
          sales: this.balanceData.betAmount || 0,
          commission: this.balanceData.comissionAmount || 0,
          net: this.balanceData.netAmount || 0,
          retainedPrizes: this.balanceData.retainedPrizes || 0,
          prizes: this.balanceData.totalAwarded || 0, // Corrigido: usar totalAwarded em vez de prizes
          netWithPrize: this.balanceData.netWithPrizeAmount || 0,
          credit: this.balanceData.creditAmount || 0,
          debit: this.balanceData.debitAmount || 0,
          currentBalance: this.balanceData.currentBalanceAmount || 0
        };
      } catch (error) {
        this.resetFinancialSummary();
      }
    },

    async calculateSellersSummary() {
      try {
        if (!Array.isArray(this.sellersData)) {
          this.resetSellersSummary();
          return;
        }

        // Contar vendedores ativos
        const activeSellers = this.sellersData.filter(seller => seller && seller.isActive);
        this.sellersSummary.totalActive = activeSellers.length;

        if (activeSellers.length === 0) {
          this.sellersSummary.activeWithSales = 0;
          this.sellersSummary.activeWithoutSales = 0;
          return;
        }

        // Consultar o balanço individual no período. O resumo por região não permite
        // identificar quais vendedores realmente venderam.
        const response = await mainService.searchSellerBalance({
          sellerIds: activeSellers.map(seller => seller.id),
          balanceDateBegin: this.dateBegin + 'T00:00:00',
          balanceDateEnd: this.dateEnd + 'T23:59:59'
        });

        const activeSellerIds = new Set(
          activeSellers.map(seller => String(seller.id).toLowerCase())
        );
        const sellerIdsWithSales = new Set(
          (response.data?.sellers || [])
            .filter(item =>
              item?.sellerId &&
              activeSellerIds.has(String(item.sellerId).toLowerCase()) &&
              Number(item.bet || 0) > 0
            )
            .map(item => String(item.sellerId).toLowerCase())
        );

        this.sellersSummary.activeWithSales = sellerIdsWithSales.size;
        this.sellersSummary.activeWithoutSales =
          this.sellersSummary.totalActive - sellerIdsWithSales.size;
        
      } catch (error) {
        this.resetSellersSummary();
        throw error;
      }
    },

    resetFinancialSummary() {
      this.financialSummary = {
        previousBalance: 0,
        sales: 0,
        commission: 0,
        net: 0,
        retainedPrizes: 0,
        prizes: 0,
        netWithPrize: 0,
        credit: 0,
        debit: 0,
        currentBalance: 0
      };
    },

    resetSellersSummary() {
      this.sellersSummary = {
        totalActive: 0,
        activeWithSales: 0,
        activeWithoutSales: 0
      };
    },

    resetAllData() {
      this.resetFinancialSummary();
      this.resetSellersSummary();
      this.balanceData = null;
      this.sellersData = [];
    },

    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value);
    },

    getPeriodLabel() {
      if (!this.dateBegin || !this.dateEnd) return "Período selecionado";
      if (this.dateBegin === this.dateEnd) {
        return new Date(this.dateBegin).toLocaleDateString('pt-BR');
      }
      return `${new Date(this.dateBegin).toLocaleDateString('pt-BR')} - ${new Date(this.dateEnd).toLocaleDateString('pt-BR')}`;
    },

    getBarHeight(value) {
      try {
        // Verificar se o valor é válido
        if (value === null || value === undefined || isNaN(value)) {
          return '5%';
        }

        // Encontrar o valor máximo para escalar as barras
        const values = [
          Math.abs(this.financialSummary?.previousBalance || 0),
          Math.abs(this.financialSummary?.sales || 0),
          Math.abs(this.financialSummary?.commission || 0),
          Math.abs(this.financialSummary?.net || 0),
          Math.abs(this.financialSummary?.retainedPrizes || 0),
          Math.abs(this.financialSummary?.prizes || 0),
          Math.abs(this.financialSummary?.netWithPrize || 0),
          Math.abs(this.financialSummary?.credit || 0),
          Math.abs(this.financialSummary?.debit || 0),
          Math.abs(this.financialSummary?.currentBalance || 0)
        ];
        
        const maxValue = Math.max(...values, 1);
        const percentage = (Math.abs(value) / maxValue) * 100;
        
        // Altura mínima de 5% para valores muito pequenos
        return Math.max(percentage, 5) + '%';
      } catch (error) {

        return '5%';
      }
    },

    getPercentage(value, total) {
      try {
        if (!value || !total || total === 0 || isNaN(value) || isNaN(total)) {
          return 0;
        }
        return ((value / total) * 100).toFixed(1);
      } catch (error) {

        return 0;
      }
    }
  }
};
</script>

<style scoped>
.dashboard-container {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

/* Seções do Dashboard */
.dashboard-section {
  margin-bottom: 3rem;
}

.section-title {
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid var(--border-color);
}

.section-title i {
  color: var(--accent-color);
  font-size: 1.25rem;
}

/* Grid de Estatísticas */
.dashboard-stats {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
}

/* Cards mais compactos */
.dashboard-card {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  text-align: center;
  position: relative;
  overflow: hidden;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.dashboard-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.dashboard-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
  font-weight: 600;
}

.dashboard-card-title {
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.dashboard-card-value {
  color: var(--text-primary);
  font-size: 1.375rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  line-height: 1.1;
}

.dashboard-card-subtitle {
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.2;
  opacity: 0.8;
}

/* Cards com cores específicas */
.financial-card .dashboard-card-icon {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white !important;
}

.sellers-card .dashboard-card-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white !important;
}

/* Todos os ícones dos cards devem ser brancos */
.dashboard-card-icon {
  color: white !important;
}

.dashboard-card-icon i {
  color: white !important;
}

/* Cores para valores */
.dashboard-card-icon.positive {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white !important;
}

.dashboard-card-value.positive {
  color: #10b981;
}

.dashboard-card-icon.negative {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white !important;
}

.dashboard-card-value.negative {
  color: #ef4444;
}

.dashboard-card-icon.warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white !important;
}

.dashboard-card-value.warning {
  color: #f59e0b;
}

.financial-card .dashboard-card-icon.positive {
  background: linear-gradient(135deg, #10b981, #059669);
}

.dashboard-card-icon.negative,
.dashboard-card-value.negative {
  color: #ef4444;
}

.financial-card .dashboard-card-icon.negative {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.dashboard-card-icon.warning,
.dashboard-card-value.warning {
  color: #f59e0b;
}

.sellers-card .dashboard-card-icon.warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

/* Containers de Gráfico */
.chart-container {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  margin-top: 1rem;
}

.chart-header {
  padding: 1.5rem;
  background: linear-gradient(135deg, var(--secondary-color), var(--card-background));
  border-bottom: 1px solid var(--border-color);
}

.chart-header h3 {
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chart-content {
  padding: 2rem;
}

/* Gráfico de Barras */
.chart-bars {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 250px;
  gap: 0.5rem;
  overflow-x: auto;
}

.chart-bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 80px;
  max-width: 100px;
}

.bar-label {
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  text-align: center;
  white-space: nowrap;
  line-height: 1.1;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bar-container {
  height: 180px;
  width: 32px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-bottom: 0.5rem;
  position: relative;
}

.bar {
  width: 100%;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.bar.positive {
  background: linear-gradient(180deg, #10b981, #059669);
}

.bar.negative {
  background: linear-gradient(180deg, #ef4444, #dc2626);
}

.bar.warning {
  background: linear-gradient(180deg, #f59e0b, #d97706);
}

.bar.neutral {
  background: linear-gradient(180deg, #6b7280, #4b5563);
}

.bar-value {
  color: var(--text-primary);
  font-size: 0.7rem;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  line-height: 1.1;
  min-height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Gráfico de Pizza */
.pie-chart-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3rem;
  min-height: 250px;
}

.pie-chart {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  position: relative;
  background: conic-gradient(
    from 0deg,
    #10b981 0deg,
    #10b981 calc(var(--percentage-with-sales, 0) * 3.6deg),
    #f59e0b calc(var(--percentage-with-sales, 0) * 3.6deg),
    #f59e0b 360deg
  );
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 3px solid var(--border-color);
}

.pie-legend {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-primary);
  font-weight: 500;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.legend-color.active-with-sales {
  background: linear-gradient(135deg, #10b981, #059669);
}

.legend-color.active-without-sales {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

/* Chart Placeholder */
.chart-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 250px;
  color: var(--text-muted);
  text-align: center;
}

.chart-placeholder-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
  color: var(--accent-color);
}

.chart-placeholder p {
  font-size: 1.1rem;
  margin: 0;
  color: var(--text-secondary);
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin: 2rem 0;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--text-secondary);
  font-size: 1.1rem;
}

.loading-spinner i {
  font-size: 1.5rem;
  color: var(--accent-color);
}

/* Filtros */
.filters-card {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  margin-bottom: 2rem;
}

.filters-content {
  padding: 1.5rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.filters-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

/* Filtros Soltos */
.filters-section-loose {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: var(--shadow-sm);
}

.filters-header {
  margin-bottom: 1.5rem;
}

.filters-title {
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filters-title i {
  color: var(--accent-color);
  font-size: 1.1rem;
}

.filters-grid-loose {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  align-items: end;
}

.filters-actions-loose {
  display: flex;
  justify-content: flex-end;
  align-items: end;
  min-width: 150px;
}

.filters-actions-loose .btn-primary {
  width: 100%;
  padding: 0.875rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
  text-decoration: none;
  min-height: 45px;
}

/* Responsividade */
/* Tablet - 3 colunas */
@media (max-width: 1024px) and (min-width: 769px) {
  .dashboard-stats {
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }
}

/* Mobile - 2 colunas */
@media (max-width: 768px) {
  .dashboard-container {
    padding: 1rem;
  }
  
  .filters-section-loose {
    padding: 1rem;
  }
  
  .filters-grid-loose {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .filters-actions-loose {
    justify-content: stretch;
  }
  
  .dashboard-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  .dashboard-card {
    padding: 1rem;
    min-height: 120px;
  }
  
  .dashboard-card-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
  }
  
  .dashboard-card-value {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  
  .dashboard-header {
    margin-bottom: 1.5rem;
  }
  
  .section-title {
    font-size: 1.25rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .chart-content {
    padding: 1rem;
  }
  
  .chart-bars {
    height: 200px;
    gap: 0.3rem;
  }
  
  .chart-bar {
    min-width: 60px;
    max-width: 70px;
  }
  
  .bar-container {
    height: 140px;
    width: 26px;
  }
  
  .bar-label {
    font-size: 0.65rem;
    height: 1.8rem;
  }
  
  .bar-value {
    font-size: 0.6rem;
    min-height: 1.5rem;
  }
  
  .pie-chart-container {
    flex-direction: column;
    gap: 2rem;
  }
  
  .pie-chart {
    width: 150px;
    height: 150px;
  }
  
  .filters-grid {
    grid-template-columns: 1fr;
  }
  
  .filters-actions {
    justify-content: stretch;
  }
  
  .filters-actions .btn-primary {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .filters-section-loose {
    padding: 0.75rem;
  }
  
  .filters-grid-loose {
    gap: 0.75rem;
  }
  
  .dashboard-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
  
  .dashboard-card {
    padding: 0.875rem;
    min-height: 110px;
  }
  
  .dashboard-card-icon {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  
  .dashboard-card-title {
    font-size: 0.8rem;
  }
  
  .dashboard-card-value {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  
  .dashboard-card-subtitle {
    font-size: 0.7rem;
  }
  
  .dashboard-section {
    margin-bottom: 2rem;
  }
  
  .chart-bars {
    height: 160px;
    gap: 0.2rem;
  }
  
  .chart-bar {
    min-width: 50px;
    max-width: 60px;
  }
  
  .bar-container {
    height: 120px;
    width: 22px;
  }
  
  .bar-label {
    font-size: 0.6rem;
    height: 1.5rem;
  }
  
  .bar-value {
    font-size: 0.55rem;
    min-height: 1.2rem;
  }
  
  .pie-chart {
    width: 120px;
    height: 120px;
  }
  
  .legend-item {
    font-size: 0.875rem;
  }
  
  .legend-color {
    width: 16px;
    height: 16px;
  }
  
  .chart-placeholder {
    height: 200px;
  }
  
  .chart-placeholder-icon {
    font-size: 3rem;
  }
  
  .chart-placeholder p {
    font-size: 1rem;
  }
}

/* Animações */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dashboard-section {
  animation: fadeIn 0.5s ease-in;
}

.chart-container {
  animation: fadeIn 0.6s ease-in;
}

.bar {
  animation: growUp 0.8s ease-out;
}

@keyframes growUp {
  from {
    height: 0;
  }
  to {
    height: var(--final-height);
  }
}
</style> 